// Painel fixo à direita: dados do pagamento dos itens marcados, comprovante (opcional), observação e o resumo da baixa
import React, { useRef, useState } from "react";
import { Alert, Card, Chip, Divider, FormControl, FormControlLabel, Grid, Icon, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { toCurrency } from "utils/NumberUtils";
import { infoAviso } from "utils/Functions";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { arredondar, diferenca, escaparHtml, hojeISO, numero } from "./baixaUtils";

const MAX_COMPROVANTE = 10 * 1024 * 1024;
const MAX_OBSERVACAO = 500;

const Linha = ({ rotulo, valor, forte }) => (
    <MDBox display="flex" justifyContent="space-between" py={0.25}>
        <MDTypography variant="caption" color="text">{rotulo}</MDTypography>
        <MDTypography variant={forte ? "button" : "caption"} fontWeight={forte ? "bold" : "medium"}>{valor}</MDTypography>
    </MDBox>
);

export default function PainelPagamento({ itens, contrato, formas, negociadores, negociadorPadrao, semNegociadores, avisos, enviando, onValor, onQuitar,
    onRemover, onLimpar, onConfirmar }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [dataPagamento, setDataPagamento] = useState(hojeISO());
    const [forma, setForma] = useState("");
    const [observacao, setObservacao] = useState("");
    const [arquivo, setArquivo] = useState(null);
    const [erroArquivo, setErroArquivo] = useState("");
    const entradaArquivo = useRef(null);
    // Acordo de pagamento (só débito avulso): o pagamento vira um acordo quitado de 1 parcela no nome do negociador, para
    // a comissão e os relatórios; não tem desconto, o que faltar continua em aberto
    const [acordoPagamento, setAcordoPagamento] = useState(false);
    const [negociador, setNegociador] = useState(negociadorPadrao || "");
    const comoAcordo = contrato && acordoPagamento;
    const quitarDe = (i) => (comoAcordo ? false : i.quitar);
    const escolhido = (negociadores || []).find(n => n.value === negociador);
    const nomeNegociador = escolhido?.label;

    const totais = itens.reduce((t, i) => {
        const { desconto, acrescimo, restante } = diferenca(i.saldo, i.valorPago, quitarDe(i));
        return {
            saldo: t.saldo + (i.saldo || 0),
            pago: t.pago + numero(i.valorPago),
            desconto: t.desconto + desconto,
            acrescimo: t.acrescimo + acrescimo,
            restante: t.restante + restante,
        };
    }, { saldo: 0, pago: 0, desconto: 0, acrescimo: 0, restante: 0 });
    const boletosAtivos = itens.filter(i => i.boletoAtivo).length;
    const valorInvalido = itens.some(i => numero(i.valorPago) <= 0);
    const dataFutura = dataPagamento > hojeISO();
    const pendencias = [
        !dataPagamento && "a data do pagamento",
        dataFutura && "uma data que não seja futura",
        !forma && "a forma de pagamento",
        valorInvalido && "o valor pago de cada item",
        comoAcordo && !negociador && "o negociador do acordo",
    ].filter(Boolean);

    const escolherArquivo = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!f) return;
        if (!/\.(pdf|jpe?g|png)$/i.test(f.name)) {
            setErroArquivo("O comprovante precisa ser PDF, JPG ou PNG.");
            return;
        }
        if (f.size > MAX_COMPROVANTE) {
            setErroArquivo("O comprovante passa de 10 MB.");
            return;
        }
        setErroArquivo("");
        setArquivo(f);
    };

    const confirmar = () => {
        const linhas = [
            comoAcordo ? `<b>Acordo de pagamento</b> no nome de <b>${escaparHtml(nomeNegociador || "")}</b> (conta como acordo e recebimento na Análise de Atendimento e nos relatórios); o débito não fica preso e a ficha continua em cobrança.<br/>` : "",
            `<b>${itens.length}</b> ${contrato ? "débito(s)" : "parcela(s)"} · pago em <b>${escaparHtml(dataPagamento.split("-").reverse().join("/"))}</b> por <b>${escaparHtml(forma)}</b>`,
            `Valor pago: <b>${toCurrency(arredondar(totais.pago))}</b>`,
            totais.desconto > 0 ? `Desconto: ${toCurrency(arredondar(totais.desconto))}` : "",
            totais.acrescimo > 0 ? `Acréscimo: ${toCurrency(arredondar(totais.acrescimo))}` : "",
            totais.restante > 0 ? `Fica em aberto (baixa parcial): <b>${toCurrency(arredondar(totais.restante))}</b>` : "",
            boletosAtivos > 0 && forma !== "Boleto" ? `${boletosAtivos} boleto(s) ativo(s) será(ão) cancelado(s).` : "",
            arquivo ? `Comprovante: ${escaparHtml(arquivo.name)}` : "",
        ].filter(Boolean);
        infoAviso(`${comoAcordo ? "Registra o acordo de pagamento?" : "Confirma a baixa?"}<br/><br/>${linhas.join("<br/>")}`,
            () => onConfirmar({ dataPagamento, formaPagamento: forma, observacao, arquivo, acordoPagamento: comoAcordo, negociador: comoAcordo ? negociador : null }));
    };

    return (
        <Card sx={{ p: 2, position: { lg: "sticky" }, top: { lg: 12 } }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                <Icon color="info">payments</Icon>
                <MDTypography variant="button" fontWeight="bold">Pagamento</MDTypography>
            </MDBox>

            {itens.length === 0 ? (
                <MDBox py={3} textAlign="center">
                    <MDTypography variant="button" color="text" display="block">
                        Marque as parcelas (ou os débitos avulsos) que foram pagas.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                        Dá para baixar várias de uma vez, todas com a mesma data e a mesma forma de pagamento.
                    </MDTypography>
                </MDBox>
            ) : (
                <>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} lg={12}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data do pagamento *</InputLabel>
                            <MDInput type="date" size="small" fullWidth value={dataPagamento} inputProps={{ max: hojeISO() }}
                                onChange={(e) => setDataPagamento(e.target.value)} error={dataFutura} />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={12}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Forma de pagamento *</InputLabel>
                            <FormControl fullWidth size="small">
                                <Select value={forma} displayEmpty onChange={(e) => setForma(e.target.value)}>
                                    <MenuItem value="" disabled>Escolha</MenuItem>
                                    {(formas || []).map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {contrato && (
                        <MDBox mt={2} p={1.5} sx={{ border: `1px solid ${cores.borda}`, borderRadius: 2 }}>
                            <FormControlLabel sx={{ ml: 0, mr: 0 }}
                                control={<Switch checked={acordoPagamento} onChange={(e) => setAcordoPagamento(e.target.checked)} />}
                                label={<MDTypography variant="button" fontWeight="medium">Registrar como acordo de pagamento</MDTypography>} />
                            <MDTypography variant="caption" color="text" display="block">
                                Para o negociador ser comissionado: o pagamento vira um acordo quitado de 1 parcela no nome do negociador (Análise de
                                Atendimento e relatórios de acordos). O débito não fica preso: abate só o que foi pago e o resto continua em cobrança.
                            </MDTypography>
                            {acordoPagamento && (
                                <MDBox mt={1.5}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Negociador *</InputLabel>
                                    <FormControl fullWidth size="small">
                                        <Select value={negociador} displayEmpty onChange={(e) => setNegociador(e.target.value)}>
                                            <MenuItem value="" disabled>Escolha</MenuItem>
                                            {(negociadores || []).map(n => (
                                                <MenuItem key={n.value} value={n.value}>
                                                    {n.label}{n.responsavel ? " (responsável da ficha)" : ""}{n.daCarteira ? "" : " · fora da carteira"}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {(negociadores || []).length === 0 ? (
                                        <Alert severity="warning" sx={{ mt: 1, fontSize: "0.8rem" }}>
                                            {semNegociadores || "Ninguém nas equipes da carteira desta ficha: acerte em Ferramentas > Equipes."}
                                        </Alert>
                                    ) : escolhido && !escolhido.daCarteira ? (
                                        <Alert severity="warning" sx={{ mt: 1, fontSize: "0.8rem" }}>
                                            {escolhido.label} não é membro nem backoffice de equipe da carteira desta ficha: o acordo sai nos relatórios
                                            de acordos, mas a Análise de Atendimento dessa carteira não o conta para essa pessoa.
                                        </Alert>
                                    ) : (
                                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                            Já vem o responsável da ficha; dá para trocar por outro usuário ativo de equipe.
                                        </MDTypography>
                                    )}
                                </MDBox>
                            )}
                        </MDBox>
                    )}

                    <MDBox mt={2}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Valor pago de cada item</InputLabel>
                        {itens.map(i => {
                            const { desconto, acrescimo } = diferenca(i.saldo, i.valorPago, quitarDe(i));
                            // Quanto falta para o saldo, qualquer que seja a escolha (desconto ou baixa parcial)
                            const falta = diferenca(i.saldo, i.valorPago, true).desconto;
                            return (
                                <MDBox key={i.id} py={0.75} sx={{ borderBottom: `1px solid ${cores.divisoria || cores.borda}` }}>
                                    <MDBox display="flex" alignItems="flex-start" gap={1}>
                                        <MDBox flex={1} minWidth={0}>
                                            <MDTypography variant="button" fontWeight="medium" display="block">{i.titulo}</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {i.detalhe ? `${i.detalhe} · ` : ""}saldo {toCurrency(i.saldo)}
                                            </MDTypography>
                                        </MDBox>
                                        <MDInput size="small" value={i.valorPago} onChange={(e) => onValor(i.id, e.target.value)}
                                            inputProps={{ inputMode: "decimal", "aria-label": `Valor pago de ${i.titulo}`, style: { textAlign: "right" } }}
                                            sx={{ width: 120 }} error={numero(i.valorPago) <= 0} />
                                        <Tooltip title="Tirar da baixa">
                                            <IconButton size="small" onClick={() => onRemover(i.id)} aria-label={`Tirar ${i.titulo} da baixa`}>
                                                <Icon fontSize="small">close</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                    {comoAcordo && falta > 0 && (
                                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                            {toCurrency(falta)} continua em aberto (o acordo de pagamento não tem desconto)
                                        </MDTypography>
                                    )}
                                    {contrato && !comoAcordo && falta > 0 && (
                                        <RadioGroup value={i.quitar ? "quitar" : "parcial"} onChange={(e) => onQuitar(i.id, e.target.value === "quitar")}
                                            aria-label={`O que fazer com a diferença de ${i.titulo}`} sx={{ mt: 0.5, pl: 0.5 }}>
                                            <FormControlLabel value="parcial" control={<Radio size="small" sx={{ p: 0.5 }} />} sx={{ mr: 0 }}
                                                label={<MDTypography variant="caption" color="text">Baixa parcial: {toCurrency(falta)} continua em aberto</MDTypography>} />
                                            <FormControlLabel value="quitar" control={<Radio size="small" sx={{ p: 0.5 }} />} sx={{ mr: 0 }}
                                                label={<MDTypography variant="caption" color="text">Desconto de {toCurrency(falta)}: quita o débito</MDTypography>} />
                                        </RadioGroup>
                                    )}
                                    {!contrato && desconto > 0 && (
                                        <MDTypography variant="caption" color="warning" display="block">
                                            Desconto de {toCurrency(desconto)}: o item fica quitado
                                        </MDTypography>
                                    )}
                                    {acrescimo > 0 && (
                                        <MDTypography variant="caption" color="info" display="block">Acréscimo de {toCurrency(acrescimo)} (juros, multa)</MDTypography>
                                    )}
                                </MDBox>
                            );
                        })}
                    </MDBox>

                    {boletosAtivos > 0 && forma && forma !== "Boleto" && (
                        <Alert severity="warning" sx={{ mt: 1.5, fontSize: "0.8rem" }}>
                            {boletosAtivos} parcela(s) com boleto ativo: o boleto é cancelado e, se já foi ao banco, o pedido de baixa vai na próxima remessa.
                        </Alert>
                    )}
                    {boletosAtivos > 0 && forma === "Boleto" && (
                        <Alert severity="info" sx={{ mt: 1.5, fontSize: "0.8rem" }}>
                            Pago pelo próprio boleto: ele não é cancelado.
                        </Alert>
                    )}
                    {(avisos || []).map(a => (
                        <Alert key={a} severity="info" sx={{ mt: 1.5, fontSize: "0.8rem" }}>{a}</Alert>
                    ))}

                    <MDBox mt={2}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Comprovante (opcional)</InputLabel>
                        <input ref={entradaArquivo} type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={escolherArquivo} />
                        {arquivo ? (
                            <Chip icon={<Icon>attach_file</Icon>} label={arquivo.name} onDelete={() => setArquivo(null)} variant="outlined"
                                sx={{ maxWidth: "100%" }} />
                        ) : (
                            <MDButton variant="outlined" color="info" size="small" onClick={() => entradaArquivo.current && entradaArquivo.current.click()}>
                                <Icon sx={{ mr: 0.5 }}>upload_file</Icon> Anexar comprovante
                            </MDButton>
                        )}
                        <MDTypography variant="caption" color={erroArquivo ? "error" : "text"} display="block" mt={0.5}>
                            {erroArquivo || "PDF, JPG ou PNG até 10 MB. Vai para a aba Documentos da ficha, ligado à ocorrência da baixa."}
                        </MDTypography>
                    </MDBox>

                    <MDBox mt={2}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Observação (opcional)</InputLabel>
                        <MDInput multiline rows={2} fullWidth value={observacao} placeholder="Vai no texto da ocorrência"
                            onChange={(e) => setObservacao(e.target.value.slice(0, MAX_OBSERVACAO))} />
                        <MDTypography variant="caption" color="text" display="block" textAlign="right">{observacao.length}/{MAX_OBSERVACAO}</MDTypography>
                    </MDBox>

                    <MDBox mt={1.5} p={1.5} sx={{ backgroundColor: cores.painel, borderRadius: 2, border: `1px solid ${cores.borda}` }}>
                        <Linha rotulo={contrato ? "Débitos" : "Parcelas"} valor={itens.length} />
                        <Linha rotulo="Saldo dos itens" valor={toCurrency(arredondar(totais.saldo))} />
                        {totais.desconto > 0 && <Linha rotulo="Desconto" valor={toCurrency(arredondar(totais.desconto))} />}
                        {totais.acrescimo > 0 && <Linha rotulo="Acréscimo" valor={toCurrency(arredondar(totais.acrescimo))} />}
                        <Divider sx={{ my: 0.75 }} />
                        <Linha rotulo="Valor pago" valor={toCurrency(arredondar(totais.pago))} forte />
                        {totais.restante > 0 && <Linha rotulo="Fica em aberto (baixa parcial)" valor={toCurrency(arredondar(totais.restante))} />}
                    </MDBox>

                    {pendencias.length > 0 && (
                        <MDTypography variant="caption" color="text" display="block" mt={1}>Falta informar {pendencias.join(", ")}.</MDTypography>
                    )}
                    <MDBox display="flex" gap={1} mt={1.5}>
                        <MDButton variant="outlined" color="secondary" onClick={onLimpar} disabled={enviando} sx={{ flex: 1 }}>Limpar</MDButton>
                        <MDButton variant="contained" color="success" onClick={confirmar} disabled={enviando || pendencias.length > 0} sx={{ flex: 2 }}>
                            <Icon sx={{ mr: 0.5 }}>check</Icon>{enviando ? "Baixando..." : comoAcordo ? "Registrar acordo" : "Confirmar baixa"}
                        </MDButton>
                    </MDBox>
                </>
            )}
        </Card>
    );
}
